import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import './Login.css';

// Keep the shortcut opt-in for production releases used during development.
const DEMO_CREDENTIALS = {
  email: process.env.REACT_APP_DEV_ADMIN_EMAIL || '',
  password: process.env.REACT_APP_DEV_ADMIN_PASSWORD || '',
};

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      console.log('🔐 Login Form: Attempting login with:', { email: formData.email, password: '***' });

      const result = await login(formData.email, formData.password);

      console.log('🔐 Login Form: Login result:', result);

      if (result.success) {
        console.log('🔐 Login Form: Login successful, navigating to:', from);
        // Redirect to intended page or home
        navigate(from, { replace: true });
      } else {
        console.log('🔐 Login Form: Login failed with error:', result.error);
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.log('🔐 Login Form: Unexpected error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickLogin = async () => {
    if (isLoading) return;

    setFormData(DEMO_CREDENTIALS);
    setIsLoading(true);
    setErrors({});

    try {
      const result = await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setErrors({ general: result.error });
      }
    } catch {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="auth-login-page">
      <div className="auth-login-layout">
        <section className="auth-login-intro" aria-labelledby="auth-login-title">
          <h1 id="auth-login-title">Sign into your Account</h1>
          <p className="auth-login-description">
            Continue your learning and career journey with Gauteng’s skills and training opportunities.
          </p>
        </section>

        <section className="auth-login-card" aria-label="Account sign in">
          <form className="auth-login-form" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="auth-login-error" role="alert">
                {errors.general}
              </div>
            )}

            <div className="auth-login-field">
              <label htmlFor="email">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={errors.email ? 'has-error' : ''}
                placeholder="name@example.co.za"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="auth-login-field-error">{errors.email}</p>}
            </div>

            <div className="auth-login-field">
              <label htmlFor="password">
                Password
              </label>
              <div className="auth-login-password">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={errors.password ? 'has-error' : ''}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="auth-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon />
                  ) : (
                    <EyeIcon />
                  )}
                </button>
              </div>
              {errors.password && <p className="auth-login-field-error">{errors.password}</p>}
            </div>

            <div className="auth-login-options">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            {(process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_ADMIN_QUICK_LOGIN === 'true') && DEMO_CREDENTIALS.email && DEMO_CREDENTIALS.password && (
              <button
                type="button"
                className="auth-login-quick"
                onClick={handleQuickLogin}
                disabled={isLoading}
              >
                Quick login with admin credentials
              </button>
            )}

            <div className="auth-login-actions">
              <button
                type="submit"
                disabled={isLoading}
                className="auth-login-submit"
              >
                {isLoading ? (
                  <span className="auth-login-spinner-wrap">
                    <span className="auth-login-spinner" aria-hidden="true" />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="auth-login-register">
            <span>New to GMSP3 Career Guidance?</span>
            <Link to="/register">Create an account</Link>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Login;
